import { MUTE_DURATIONS, muteLabel } from '@devrobot/shared';
import { Bell, BellOff } from 'lucide-react';
import { useNotificationPrefs, useResumeProject, useSilenceProject } from '../../api/notifications';
import { MenuPopover } from '../MenuPopover';
import { RelativeTime } from '../RelativeTime';

/**
 * Silences one project for the person looking at it.
 *
 * Only offered when they actually have an interrupting channel switched on: silencing
 * nothing is a confusing control. A silence stops the interruptions, never the record —
 * the inbox keeps filling, which is what makes it safe to leave on indefinitely.
 */
export function SilenceMenu({ projectKey }: { projectKey: string }) {
  const prefs = useNotificationPrefs();
  const silence = useSilenceProject();
  const resume = useResumeProject();

  if (!prefs.data) return null;
  const interrupting = prefs.data.categories.some((c) => c.push || c.rocketchat);
  if (!interrupting) return null;

  const mute = prefs.data.mutes.find((m) => m.projectKey === projectKey || m.projectKey === '*');
  const everything = mute?.projectKey === '*';
  const busy = silence.isPending || resume.isPending;

  return (
    <MenuPopover
      label={mute ? `Notifications silenced for ${projectKey}` : `Silence notifications for ${projectKey}`}
      triggerClassName={`btn btn-sm ${mute ? 'btn-warning btn-soft' : 'btn-ghost'}`}
      panelClassName="w-72"
      trigger={
        <>
          {mute ? <BellOff className="size-4" aria-hidden="true" /> : <Bell className="size-4" aria-hidden="true" />}
          <span className="max-sm:hidden">
            {mute ? (mute.until ? <>Silenced until <RelativeTime value={mute.until} /></> : 'Silenced') : 'Silence'}
          </span>
        </>
      }
    >
      {(close) => (
        <div className="p-1">
          {mute ? (
            <>
              <p className="px-2 py-1.5 text-xs text-base-content/70">
                {everything
                  ? 'Every project is silenced from your notification settings.'
                  : mute.until
                    ? <>Silenced until <RelativeTime value={mute.until} />.</>
                    : 'Silenced until you turn it back on.'}
              </p>
              <button
                type="button"
                className="btn btn-ghost btn-sm btn-block justify-start"
                disabled={busy || everything}
                onClick={() => {
                  resume.mutate(projectKey);
                  close();
                }}
              >
                <Bell className="size-4" aria-hidden="true" />
                Turn notifications back on
              </button>
            </>
          ) : (
            <>
              <p className="px-2 py-1.5 text-xs text-base-content/70">Stop push and chat messages for {projectKey}.</p>
              <ul className="menu menu-sm w-full p-0">
                {MUTE_DURATIONS.map((minutes) => (
                  <li key={String(minutes)}>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        silence.mutate({ projectKey, minutes });
                        close();
                      }}
                    >
                      {muteLabel(minutes)}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
          <p className="border-t border-hairline px-2 pt-2 pb-1 text-[0.6875rem] text-base-content/55">
            Notifications still appear in your inbox while a project is silenced.
          </p>
        </div>
      )}
    </MenuPopover>
  );
}
